import { createContext, useContext, useEffect, useState } from "react"

export type Theme = "dark" | "light" | "system"

const THEMES: Theme[] = ["dark", "light", "system"]

export function isValidTheme(value: string): value is Theme {
	return THEMES.includes(value as Theme)
}

type ThemeProviderProps = {
	children: React.ReactNode
	defaultTheme?: Theme
	storageKey?: string
}

type ThemeProviderState = {
	theme: Theme
	resolvedTheme: "light" | "dark"
	setTheme: (theme: Theme) => void
}

const initialState: ThemeProviderState = {
	theme: "system",
	resolvedTheme: "light",
	setTheme: () => null,
}

const ThemeProviderContext = createContext<ThemeProviderState>(initialState)

export function ThemeProvider({
	children,
	defaultTheme = "system",
	storageKey = "breakout-ui-theme",
}: ThemeProviderProps) {
	const [theme, setTheme] = useState<Theme>(
		() => (localStorage.getItem(storageKey) as Theme) || defaultTheme,
	)
	const [resolvedTheme, setResolvedTheme] = useState<"light" | "dark">("light")

	useEffect(() => {
		const root = window.document.documentElement

		root.classList.remove("light", "dark")

		if (theme === "system") {
			const systemTheme = window.matchMedia("(prefers-color-scheme: dark)").matches
				? "dark"
				: "light"

			root.classList.add(systemTheme)
			setResolvedTheme(systemTheme)
			return
		}

		root.classList.add(theme)
		setResolvedTheme(theme)
	}, [theme])

	// Listen for system theme changes when in "system" mode
	useEffect(() => {
		if (theme !== "system") {
			return
		}

		const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)")
		const handleChange = (e: MediaQueryListEvent) => {
			const newTheme = e.matches ? "dark" : "light"
			const root = window.document.documentElement
			root.classList.remove("light", "dark")
			root.classList.add(newTheme)
			setResolvedTheme(newTheme)
		}

		mediaQuery.addEventListener("change", handleChange)
		return () => mediaQuery.removeEventListener("change", handleChange)
	}, [theme])

	const value = {
		theme,
		resolvedTheme,
		setTheme: (theme: Theme) => {
			localStorage.setItem(storageKey, theme)
			setTheme(theme)
		},
	}

	return <ThemeProviderContext.Provider value={value}>{children}</ThemeProviderContext.Provider>
}

export const useTheme = () => {
	const context = useContext(ThemeProviderContext)

	if (context === undefined) {
		throw new Error("useTheme must be used within a ThemeProvider")
	}

	return context
}
